import * as React from "react";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { FormControl, styled } from "@mui/material";
import { MeasurementUnits, QualifiedFeature } from "fe-common/graphql";
import { MeasurementUnitsToString } from "../../../../../utils/dataHelpers";
import {
  numericTimingConfig,
  NumericTimingConfig,
} from "../numericTimingConfig";
import { NUMERIC_VALUE_FEATURE_ID, Relations } from "shared";
import { NumericInputView } from "./numericInputView";
import { buildQualifierValue } from "fe-common/logic";
import clsx from "clsx";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { PatientFacingDropDown } from "fe-web-components/menus/patientFacingDropDown";

//region [[ Styles ]]
const DurationPickerView = styled(FormControl)({
  display: "flex",
  flexDirection: "row",
  // height: "42px",
  width: "100%",
  transition: "all 0.5s ease-out",
  "&.disabled": {
    opacity: 0.5,
  },
});

const DurationNumericInputView = styled(NumericInputView)({
  width: "100px",
  borderRadius: "4px",
  flex: 1,
});

const UnitSelector = styled(PatientFacingDropDown)({
  flex: 2,
});

const ConnectingText = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  margin: "5px",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface DurationPickerProps {
  children?: React.ReactNode;
  className?: string;
  attachToFeature: QualifiedFeature;
  onDurationPicked: (qf: QualifiedFeature) => void;
  config?: NumericTimingConfig;
  debug?: boolean;
  disabled?: boolean;
  copyDurationFrom?: QualifiedFeature;
  durationInputRef?: RefObject<any>;
  isEmbedded?: boolean;
}

//endregion [[ Props ]]
const units = [
  MeasurementUnits.Hours,
  MeasurementUnits.Days,
  MeasurementUnits.Weeks,
  MeasurementUnits.Months,
  MeasurementUnits.Years,
];
//region [[ Functions ]]
//endregion [[ Functions ]]

export const DurationPicker = ({
  config = numericTimingConfig[Relations.DURATION],
  attachToFeature,
  onDurationPicked,
  copyDurationFrom,
  durationInputRef,
  ...props
}: DurationPickerProps) => {
  const [durationValue, setDurationValue] = useState<number>();
  const [unit, setUnit] = useState<MeasurementUnits>(null);
  const { translate } = useTranslate();
  /*Due to a bug with the select that can't hide the label once it is not shown*/
  const [lastId, setLastId] = useState(null);

  useEffect(() => {
    if (copyDurationFrom && copyDurationFrom?.qualifiers?.[0]?.range) {
      // If we have new feature to copy duration from (last in a list for example)
      // make sure to copy the units and duration number from it.
      const range = copyDurationFrom?.qualifiers?.[0]?.range;
      const { value, unit: unit_value } = config.featureToRange(range);
      if (value && value > 0) {
        setDurationValue(value);
      }
      if (unit_value) {
        setUnit(unit_value);
      }
    } else {
      setDurationValue(null);
      setUnit(null);
    }
  }, [copyDurationFrom, config]);

  const updateParent = useCallback(
    (basedOn, rangeBuilder, val, unit) => {
      if (!basedOn) return null;
      // Construct a qualified feature based on the one provided, and attach the duration range to the
      const qf = {
        ...basedOn,
        ...(basedOn?.qualifiers?.slice(1) || []),
        qualifiers: [
          {
            ...(basedOn?.qualifiers?.[0] || []),
            range: rangeBuilder(val, unit),
            value: buildQualifierValue(NUMERIC_VALUE_FEATURE_ID),
          },
        ],
      } as QualifiedFeature;
      onDurationPicked(qf);
      setLastId(null);
    },
    [onDurationPicked],
  );

  const handleDurationAmountChanged = (duration) => {
    setDurationValue(parseFloat(duration));
    if (unit && attachToFeature) {
      updateParent(
        attachToFeature,
        config.rangeBuilder,
        parseFloat(duration),
        unit,
      );
    }
  };

  const handleUnitSelected = (e, _newUnitValue) => {
    const newUnitValue = _newUnitValue || (e.target.value as MeasurementUnits);
    if (newUnitValue) {
      setUnit(newUnitValue);
      if (durationValue && attachToFeature) {
        updateParent(
          attachToFeature,
          config.rangeBuilder,
          durationValue,
          newUnitValue,
        );
      }
    }
  };

  useEffect(() => {
    const range = attachToFeature?.qualifiers?.[0]?.range;
    if (
      range &&
      config.featureToRange &&
      (!lastId || lastId !== attachToFeature?.feature?.id)
    ) {
      const { value, unit: u } = config.featureToRange(range);
      if (value && value > 0) setDurationValue(value);
      if (u) setUnit(u);
      // Make sure we update the initial values only if the feature changes
      setLastId(attachToFeature?.feature?.id);
    } else if (!attachToFeature) {
      setLastId(null);
    }
  }, [attachToFeature, config, lastId]);

  useEffect(() => {
    // If we have new feature (without range) and pre-defined state range, update the parent
    const range = attachToFeature?.qualifiers?.[0]?.range;
    if (!range && durationValue && unit) {
      updateParent(attachToFeature, config.rangeBuilder, durationValue, unit);
    }
  }, [attachToFeature, updateParent, config, durationValue, unit]);

  const unitOptions = useMemo(
    () =>
      units.map((u) => ({
        name: MeasurementUnitsToString(
          u,
          translate,
          null,
          config.unitsInPlural,
        ),
        value: u,
      })),
    [config, translate],
  );

  return (
    <DurationPickerView
      className={clsx(
        "duration-picker",
        props.className,
        props.disabled ? "disabled" : "",
      )}
      variant="outlined"
    >
      <DurationNumericInputView
        className={"numeric-input-view"}
        value={durationValue}
        onChange={handleDurationAmountChanged}
        placeholder={translate("NUMERIC_DURATION")}
        ref={durationInputRef}
      />
      <ConnectingText>
        {config.connectingText ? translate(config.connectingText) : ""}
      </ConnectingText>
      <UnitSelector
        options={unitOptions}
        value={unit}
        onValueChanged={handleUnitSelected}
        placeholder={translate("SELECT_DURATION")}
        disabled={props.disabled}
        isEmbedded={props.isEmbedded}
      />
    </DurationPickerView>
  );
};
